/**
 * Form for creating a recurring scan schedule with cron expression.
 */

import { useState, type FormEvent } from 'react';
import type { Asset, } from '@/types/asset';
import type { ScanType, ScanSchedule, ScanScheduleCreateRequest } from '@/types/scan';
import { isValidCron } from '@/utils/validators';
import { engines } from '@/constants/scan';
import { useAgents } from '@/hooks/useAgents';
import { coreApiMode } from '@/config/deployment';
import type { ScannerEngine } from '@/types/scan';

interface ScanSchedulerProps {
  assets: Asset[];
  onSubmit: (payload: ScanScheduleCreateRequest) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** When provided the form pre-populates for editing an existing schedule. */
  initialSchedule?: ScanSchedule;
}

/** Preset cron schedules for convenience. */
const presets = [
  { label: 'Daily at midnight', cron: '0 0 * * *' },
  { label: 'Weekly (Sunday midnight)', cron: '0 0 * * 0' },
  { label: 'Monthly (1st at midnight)', cron: '0 0 1 * *' },
  { label: 'Custom', cron: '' },
];

export default function ScanScheduler({ assets, onSubmit, onCancel, isSubmitting, initialSchedule }: ScanSchedulerProps) {
  const isEditing = !!initialSchedule;
  const [name, setName] = useState(initialSchedule?.name ?? '');
  const [scanType, setScanType] = useState<ScanType>(initialSchedule?.scan_type ?? 'full');
  const [cronExpression, setCronExpression] = useState(initialSchedule?.cron_expression ?? '0 0 * * 0');
  const [selectedAssets, setSelectedAssets] = useState<string[]>(initialSchedule?.target_asset_ids ?? []);
  const [selectedEngines,setSelectedEngines]=useState<ScannerEngine[]>(initialSchedule?.configs?.filter(c=>c.enabled).map(c=>c.scanner_engine)??[]);
  const [agentId,setAgentId]=useState(initialSchedule?.agent_id??'');
  const {data:agents=[]}=useAgents();
  const [error, setError] = useState('');

  function toggleAsset(id: string) {
    setSelectedAssets((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Schedule name is required.'); return; }
    if (!isValidCron(cronExpression)) { setError('Invalid cron expression.'); return; }
    if ((!isEditing || coreApiMode) && selectedAssets.length === 0) { setError('Select at least one asset.'); return; }
    if (scanType==='custom'&&!selectedEngines.length){setError('Choose at least one scanner engine.');return;}

    onSubmit({
      name: name.trim(),
      scan_type: scanType,
      cron_expression: cronExpression,
      target_asset_ids: selectedAssets,
      ...(coreApiMode?{agent_id:agentId||null}:{}),
      ...(scanType==='custom'?{configs:selectedEngines.map(scanner_engine=>({scanner_engine,enabled:true,parameters:initialSchedule?.configs?.find(c=>c.scanner_engine===scanner_engine)?.parameters??{}}))}:{}),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        <label htmlFor="schedule-name" className="block text-sm font-medium text-gray-700">Schedule Name</label>
        <input
          id="schedule-name"
          className="input-field mt-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Weekly Full Scan"
        />
      </div>

      <div>
        <label htmlFor="schedule-type" className="block text-sm font-medium text-gray-700">Scan Type</label>
        <select
          id="schedule-type"
          className="input-field mt-1"
          value={scanType}
          onChange={(e) => setScanType(e.target.value as ScanType)}
        >
          <option value="full">Full Scan</option>
          <option value="discovery">Discovery</option>
          <option value="vulnerability">Vulnerability</option>
          <option value="web">Web Application</option>
          <option value="ssl">SSL/TLS</option>
          {coreApiMode&&<option value="custom">Custom</option>}
        </select>
      </div>

      <div>
        <label htmlFor="schedule-cron" className="block text-sm font-medium text-gray-700">Schedule (UTC)</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => { if (p.cron) setCronExpression(p.cron); }}
              className={`rounded-md border px-3 py-1.5 text-xs ${
                cronExpression === p.cron ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <input
          id="schedule-cron"
          className="input-field mt-2 font-mono text-sm"
          value={cronExpression}
          onChange={(e) => setCronExpression(e.target.value)}
          placeholder="* * * * * (min hour day month weekday)"
        />
      </div>

      {(!isEditing||coreApiMode) && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Target Assets</label>
          <div className="mt-2 max-h-36 space-y-1 overflow-y-auto rounded-md border p-2">
            {assets.filter((a) => a.is_active).map((asset) => (
              <label key={asset.id} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedAssets.includes(asset.id)}
                  onChange={() => toggleAsset(asset.id)}
                />
                <span className="text-sm text-gray-700">{asset.label ?? asset.value}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {coreApiMode&&<label className="block text-sm font-medium text-gray-700">Execution location
        <select className="input-field mt-1" value={agentId} onChange={e=>setAgentId(e.target.value)}>
          <option value="">Cloud</option>
          {agents.map(agent=><option key={agent.id} value={agent.id}>{agent.name} ({agent.status})</option>)}
        </select>
      </label>}
      {scanType==='custom'&&<fieldset className="space-y-2"><legend className="text-sm font-medium text-gray-700">Scanner Engines</legend>
        {engines.map(engine=><label className="flex items-center gap-2" key={engine.value}><input type="checkbox" checked={selectedEngines.includes(engine.value)} onChange={()=>setSelectedEngines(previous=>previous.includes(engine.value)?previous.filter(value=>value!==engine.value):[...previous,engine.value])}/>{engine.label}</label>)}
      </fieldset>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Schedule'}
        </button>
      </div>
    </form>
  );
}
